import React, { useEffect, useMemo, useState } from 'react';
import DrawerShell from '@/components/DrawerShell';
import { Button } from '@/components/ui/button';
import { CalendarPlus, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { rosterApi } from '../api/roster.api';
import RosterShiftFormFields from './RosterShiftFormFields';

function emptyForm(track) {
  const p = track.presets?.[0];
  return {
    staff_id: '',
    shift_start: p?.start || '09:00',
    shift_end: p?.end || '13:00',
    location: track.defaultLocation || p?.location || '',
    slot_minutes: 15,
    max_walkins: '',
    published: true,
    recurring: false,
    days_of_week: [0, 1, 2, 3, 4],
    to_date: '',
  };
}

export default function RosterCreatePanel({
  open, onClose, date, entry, track, staffList, specialisationFilter,
  preselectedStaffId, toIso, onSaved,
}) {
  const [form, setForm] = useState(() => emptyForm(track));
  const [saving, setSaving] = useState(false);
  const editing = Boolean(entry?.id);
  const accent = track.accent;

  const filtered = useMemo(() => {
    if (!specialisationFilter || !track.bookingAware) return staffList;
    return staffList.filter((d) => d.specialization === specialisationFilter);
  }, [staffList, specialisationFilter, track.bookingAware]);

  useEffect(() => {
    if (!open) return;
    if (entry) {
      setForm({
        ...emptyForm(track),
        staff_id: entry.staff_id,
        shift_start: entry.shift_start,
        shift_end: entry.shift_end,
        location: entry.location || '',
        slot_minutes: entry.slot_minutes || 15,
        max_walkins: entry.max_walkins ?? '',
        published: !!entry.published,
        to_date: toIso,
      });
    } else {
      setForm({ ...emptyForm(track), staff_id: preselectedStaffId || '', recurring: true, to_date: toIso });
    }
  }, [open, entry, toIso, preselectedStaffId, track]);

  const selected = filtered.find((d) => d.id === form.staff_id)
    || staffList.find((d) => d.id === form.staff_id);

  const toggleDay = (v) => {
    setForm((f) => {
      const set = new Set(f.days_of_week);
      if (set.has(v)) set.delete(v); else set.add(v);
      return { ...f, days_of_week: Array.from(set).sort() };
    });
  };

  const save = async () => {
    if (!form.staff_id || !selected) {
      toast.error(`Select a ${track.staffNoun}`);
      return;
    }
    setSaving(true);
    const base = {
      role: track.id,
      staff_id: form.staff_id,
      staff_name: selected.name,
      specialisation: selected.specialization || '',
      shift_start: form.shift_start,
      shift_end: form.shift_end,
      location: form.location,
      slot_minutes: track.bookingAware ? (Number(form.slot_minutes) || 15) : 0,
      published: form.published,
    };
    if (track.bookingAware && form.max_walkins !== '' && form.max_walkins != null) {
      base.max_walkins = Number(form.max_walkins);
    }
    try {
      if (editing) {
        await rosterApi.update(entry.id, base);
        toast.success('Shift updated');
      } else if (form.recurring) {
        const res = await rosterApi.recurring({
          ...base, from_date: date, to_date: form.to_date || toIso, days_of_week: form.days_of_week,
        });
        toast.success(`Created ${res.inserted} shifts (${res.skipped_duplicates} skipped)`);
      } else {
        await rosterApi.create({ ...base, date });
        toast.success('Shift created');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save shift');
    }
    setSaving(false);
  };

  const remove = async () => {
    if (!editing || !window.confirm('Delete this shift?')) return;
    setSaving(true);
    try {
      await rosterApi.remove(entry.id);
      toast.success('Deleted');
      onSaved();
      onClose();
    } catch (_) {
      toast.error('Delete failed');
    }
    setSaving(false);
  };

  return (
    <DrawerShell
      open={open} onClose={onClose} eyebrow={track.label}
      title={editing ? 'Edit shift' : 'Create duty shift'}
      subtitle={editing ? `${entry.date} · ${entry.staff_name}` : `Starting ${date}`}
      accent={accent} icon={<CalendarPlus className="h-5 w-5" />} width="md" testId="roster-create-drawer"
      footer={(
        <>
          {editing && (
            <Button variant="outline" onClick={remove} className="text-red-700 border-red-200 mr-auto">
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ background: accent }} className="text-white" data-testid="roster-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {form.recurring && !editing ? 'Create pattern' : 'Save shift'}
          </Button>
        </>
      )}
    >
      <RosterShiftFormFields
        track={track} form={form} setForm={setForm} staffList={filtered}
        editing={editing} toIso={toIso} accent={accent} toggleDay={toggleDay}
        applyPreset={(p) => setForm((f) => ({ ...f, shift_start: p.start, shift_end: p.end, location: p.location }))}
      />
    </DrawerShell>
  );
}
